import {
	SsuLogoPlaceholder,
	useFormatAmount,
	useMeFavouritePartner,
	type WalletLedger,
	type WalletWithTotalAmountDTO
} from '@capsicum/common';
import {
	ShortCode,
	useMainCurrenciesQuery,
	usePartners,
	useWalletFormatter,
	useWalletQuery
} from '@capsicum/socios-core';
import { useCallback } from 'react';

import {
	TypeCurrency,
	type WalletConvertedPrices,
	type WalletFanToken,
	type WalletFanTokens,
	type WalletTokenTotalBalance
} from '../models';

/**
 *
 * @returns all fantoken info from user
 */
export const useWalletFanTokens = () => {
	const { formatBalance, formatTotalBalance } = useWalletFormatter();
	const { formatAmount } = useFormatAmount();
	const { favoritePartner } = useMeFavouritePartner();
	const { getPartnerBy } = usePartners();
	const favouritePartnerCurrencyId = getPartnerBy(p => p.id === favoritePartner?.id)?.currencyId;

	const { data: currencies } = useMainCurrenciesQuery();
	const { altCurrency, defaultCurrency } = currencies ?? {};

	const walletFanTokensReducer = useCallback(
		(data: undefined | WalletWithTotalAmountDTO) => {
			let favouriteFanToken = {} as WalletFanToken;
			let loyaltyToken = {} as WalletFanToken;
			const fanTokens: Array<WalletFanToken> = [];

			const totalBalance: WalletTokenTotalBalance = {
				formattedTotalBalanceOfFanTokens: formatAmount(data?.totalNumberOfFanToken),
				rawTotalBalanceOfFanTokens: data?.totalNumberOfFanToken,
				walletConvertedPrices: {
					altCurrency: formatTotalBalance(data, altCurrency),
					defaultCurrency: formatTotalBalance(data, defaultCurrency)
				}
			};

			data?.ledgers?.map(
				(ledger: WalletLedger): WalletFanTokens => {
					const isFavouritePartnerToken = ledger?.currency?.id === favouritePartnerCurrencyId;

					const isLoyaltyPartnerToken = ledger?.currency?.shortCode === ShortCode.SSU;

					const partner = getPartnerBy(p => p.currencyId === ledger.currency?.id);

					const walletConvertedPrices: WalletConvertedPrices = {
						altCurrency: formatBalance(altCurrency, ledger),
						defaultCurrency: formatBalance(defaultCurrency, ledger)
					};

					const totalTokenBalance = Number(ledger?.balance || 0) + Number(ledger?.lockedBalance || 0);

					const isFantoken = ledger?.currency?.type === TypeCurrency.VoteToken;
					const isExternalToken = ledger?.currency?.type === TypeCurrency.ExternalToken;

					const totaRoundedExternalTokenBalance = Number(totalTokenBalance?.toFixed(2));

					const fanToken = {
						availableTokenBalance: Number(ledger?.balance || 0).toString(),
						ledger,
						partner: {
							blurHash: partner?.images?.partnerLogo?.blurHash,
							id: partner?.id,
							logo: partner?.images?.partnerLogo?.url || (SsuLogoPlaceholder as unknown as string),
							name: partner?.name
						},
						totalBalance: isExternalToken ? totaRoundedExternalTokenBalance : totalTokenBalance,
						walletConvertedPrices
					};

					if (isFavouritePartnerToken) {
						favouriteFanToken = fanToken;
					} else if (isLoyaltyPartnerToken) {
						loyaltyToken = fanToken;
					} else if ((totalTokenBalance > 0 && isFantoken) || isExternalToken) {
						fanTokens.push(fanToken);
					}

					return {
						fanTokens,
						favouriteFanToken,
						loyaltyToken,
						totalBalance
					};
				},
				{
					fanTokens: {},
					favouriteFanToken: {},
					loyaltyToken: {},
					totalBalance: {}
				} as WalletFanTokens
			);

			//TODO: temporary until BE fix
			if (!favouriteFanToken?.partner?.id && favoritePartner) {
				favouriteFanToken = {
					ledger: {
						currency: {
							id: favoritePartner?.currencyId || '',
							shortCode: favoritePartner?.shortName
						}
					},
					partner: {
						id: favoritePartner?.id,
						logo: favoritePartner?.images?.partnerLogo?.url,
						name: favoritePartner?.name
					}
				};
			}

			return {
				fanTokens,
				favouriteFanToken,
				loyaltyToken,
				totalBalance
			};
		},
		[
			getPartnerBy,
			altCurrency,
			defaultCurrency,
			favouritePartnerCurrencyId,
			formatTotalBalance,
			formatBalance,
			formatAmount
		]
	);

	return useWalletQuery(walletFanTokensReducer);
};
